import { useEffect, useState } from 'react';

/**
 * Width below which the widget renders fullscreen instead of as a floating
 * corner window. The chat window is 384px wide + 16px padding each side (416px),
 * so anything narrower can't comfortably host the corner layout.
 */
const MOBILE_MAX_WIDTH = 480;

const QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`;

/**
 * Reactively tracks whether the viewport is narrow enough to warrant the
 * fullscreen mobile layout. Uses a width media query (not `pointer: coarse`)
 * so touch tablets with room for the corner window keep it.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia(QUERY).matches
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    // Sync once in case the viewport changed between initial state and effect.
    setIsMobile(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  return isMobile;
}
